import { PreferenceLine, PreferenceLineColor } from "../preferenceLine";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { useGetPickupZones } from "@/api/pickupZones.api";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditPickupZoneModal } from "./editPickupZoneModal";
import { userAtom } from "@/stores/authStore";
import { useAtomValue } from "jotai";
import { formatDate } from "date-fns";
import { Button } from "@/components/ui/button";
import { Pen, Search } from "lucide-react";
import { OpeningHours } from "@/types/forms/auth/registerFormData.form";
import { OpeningHoursDisplay } from "@/components/pickUpLocation/OpeningHoursDisplay";
import { UnifiedInput } from "@/components/ui/customs/unified-input";

type GeneralInfoPickupProps = {
  company?: GetAllCompanyDto;
};

export const GeneralInfoPickup = ({ company }: GeneralInfoPickupProps) => {
  const { t } = useTranslation("boClients");
  const user = useAtomValue(userAtom);
  const [editingPickupLocationId, setEditingPickupLocationId] = useState<string | null>(null);
  const [searchField, setSearchField] = useState("");

  const { data: pickupZones } = useGetPickupZones();

  const matchesSearch = (pickUpLocation: GetAllCompanyDto["pickUpLocations"][0]) => {
    if (!searchField.trim()) return true;
    const haystack =
      `${pickUpLocation.name} ${pickUpLocation.street} ${pickUpLocation.postalCode} ${pickUpLocation.city}`.toLowerCase();
    return haystack.includes(searchField.trim().toLowerCase());
  };

  const defaultPickupLocation = company?.pickUpLocations.find(
    (el) => el.isDefault && matchesSearch(el),
  );
  const otherPickupLocations = company?.pickUpLocations.filter(
    (el) => el.isDefault === false && matchesSearch(el),
  );

  const editingPickUpLocation = editingPickupLocationId
    ? company?.pickUpLocations.find((p) => p.id === editingPickupLocationId)
    : undefined;

  return (
    <div className="flex w-full flex-1 flex-col gap-6 lg:px-8">
      <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
        <div className="items-left flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("clientPickup.title")}</h2>
          <p className="text-sm text-gray-600">{t("clientPickup.subtitle")}</p>
        </div>
      </div>
      <UnifiedInput
        inputType="text"
        leftElement={<Search />}
        value={searchField}
        onChange={(value) => setSearchField(String(value ?? ""))}
        placeholder={t("clientPickup.searchPlaceholder")}
        wrapperClassName="w-full max-w-[400px] !h-10"
      />
      <div className="flex flex-col gap-10">
        {defaultPickupLocation && (
          <div className="flex flex-col">
            <h3 className="text-base font-medium text-black">
              {t("clientPickup.defaultPickupLocation")}
            </h3>
            <PickupEntityGroup
              {...defaultPickupLocation}
              onEditPickupZone={
                user?.role === "SUPERADMIN"
                  ? () => setEditingPickupLocationId(defaultPickupLocation.id)
                  : undefined
              }
            />
          </div>
        )}
        {otherPickupLocations?.map((pickUpLocation) => (
          <div className="flex flex-col" key={pickUpLocation.id}>
            <h3 className="text-base font-medium text-black">
              {pickUpLocation?.deletedAt
                ? t("clientPickup.deletedAdditionalPickupLocation", {
                    date: formatDate(pickUpLocation.deletedAt, "dd/MM/yyyy"),
                  })
                : t("clientPickup.additionalPickupLocation")}
            </h3>
            <PickupEntityGroup
              {...pickUpLocation}
              onEditPickupZone={
                user?.role === "SUPERADMIN"
                  ? () => setEditingPickupLocationId(pickUpLocation.id)
                  : undefined
              }
            />
          </div>
        ))}
      </div>
      <EditPickupZoneModal
        open={Boolean(editingPickUpLocation)}
        onClose={() => setEditingPickupLocationId(null)}
        pickUpLocation={editingPickUpLocation}
        pickupZonesList={pickupZones}
      />
    </div>
  );
};

type PickupEntityGroupProps = GetAllCompanyDto["pickUpLocations"][0] & {
  onEditPickupZone?: () => void;
};

const PickupEntityGroup = ({
  name,
  street,
  postalCode,
  city,
  contactNumber,
  contactNumber2,
  openingHours,
  pickupZones,
  onEditPickupZone,
}: PickupEntityGroupProps) => {
  const { t } = useTranslation("boClients");
  return (
    <div className="flex flex-col gap-0">
      <PreferenceLine title={t("clientPickup.nameLabel")} value={[name]} />
      <PreferenceLine
        title={t("clientPickup.addressLabel")}
        value={[street + ",", postalCode + " " + city]}
      />
      <PreferenceLine title={t("clientPickup.contactNumberLabel")} value={[contactNumber]} />
      <PreferenceLine
        title={t("clientPickup.contactNumber2Label")}
        value={[contactNumber2 !== "+33" ? contactNumber2 : ""]}
      />
      <div className="flex w-full flex-row items-start justify-between border-0 border-b border-gray-200 py-4">
        <div className="flex flex-1 flex-col">
          <p className="text-sm font-medium text-gray-600">{t("clientPickup.openingHoursLabel")}</p>
        </div>
        <div className="flex flex-1 flex-col">
          <OpeningHoursDisplay openingHours={openingHours as OpeningHours | undefined} />
        </div>
      </div>
      <PreferenceLineColor
        title={t("clientPickup.pickupZoneLabel")}
        value={[pickupZones?.name || t("clientPickup.undefinedPickupZone")]}
        color={pickupZones?.color}
        titleEnd={
          onEditPickupZone ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onEditPickupZone}
              aria-label={t("clientPickup.editPickupZone")}
            >
              <Pen className="text-gray-500 hover:text-black" />
            </Button>
          ) : undefined
        }
      />
    </div>
  );
};
